import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { MosaicNode } from 'react-mosaic-component';
import { DEFAULT_MOSAIC_TREE, type SaveStatus, type TileId } from '@/components/sip/mosaic/types';
import {
  loadTreeFromStorage,
  parseMosaicLayout,
  persistTreeLocally,
} from '@/components/sip/mosaic/mosaicLayoutStorage';
import { useUserConfig, useSaveUserConfig } from '@/hooks/api/useUserConfig';

const LAYOUT_CONFIG_KEY = 'sip_mosaic_layout';

export interface MosaicLayoutPersistence {
  mosaicTree: MosaicNode<TileId> | null;
  saveStatus: SaveStatus;
  isDirty: boolean;
  applyTree: (tree: MosaicNode<TileId> | null) => void;
  handleReset: () => void;
  handleSave: () => Promise<void>;
}

export function useMosaicLayoutPersistence(): MosaicLayoutPersistence {
  // Lazy initialisers — loadTreeFromStorage reads localStorage only on mount.
  const [mosaicTree, setMosaicTree] = useState<MosaicNode<TileId> | null>(loadTreeFromStorage);
  const [savedTree, setSavedTree] = useState<MosaicNode<TileId> | null>(loadTreeFromStorage);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref guard prevents concurrent saves without adding saveStatus to handleSave deps.
  const isSavingRef = useRef(false);

  // React Query handles abort-on-unmount and caching; no cancelled flag needed.
  const { data: remoteConfig } = useUserConfig(LAYOUT_CONFIG_KEY);
  const { execute: saveConfig } = useSaveUserConfig();

  const isDirty = useMemo(
    () => JSON.stringify(mosaicTree) !== JSON.stringify(savedTree),
    [mosaicTree, savedTree],
  );

  // When the backend fetch resolves, apply the remote tree (if valid).
  useEffect(() => {
    const tree = parseMosaicLayout(remoteConfig?.value);
    if (!tree) return;
    setMosaicTree(tree);
    setSavedTree(tree);
    persistTreeLocally(tree);
  }, [remoteConfig]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const applyTree = useCallback((newTree: MosaicNode<TileId> | null) => {
    setMosaicTree(newTree);
    persistTreeLocally(newTree);
  }, []);

  const handleReset = useCallback(() => {
    applyTree(DEFAULT_MOSAIC_TREE);
  }, [applyTree]);

  const handleSave = useCallback(async () => {
    if (!mosaicTree || isSavingRef.current) return;
    isSavingRef.current = true;
    setSaveStatus('saving');
    try {
      await saveConfig({ key: LAYOUT_CONFIG_KEY, value: mosaicTree });
      setSavedTree(mosaicTree);
      persistTreeLocally(mosaicTree);
      setSaveStatus('saved');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 1500);
    } catch {
      setSaveStatus('error');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
    } finally {
      isSavingRef.current = false;
    }
  }, [mosaicTree, saveConfig]);

  return { mosaicTree, saveStatus, isDirty, applyTree, handleReset, handleSave };
}
